export const NON_NUMBER_TOKEN_INDEX = -1;

export interface PreferredNumberToken {
    index: number;
    value: string;
}

const NUMBER_TOKEN_PATTERN = /^\d+$/;
const FORMULA_TOKEN_PATTERN = /\d+|[()+\-*/]/g;

const isValidNumberIndex = (
    index: number,
    token: string,
    numbers: string[],
    claimedIndices: Set<number>
): boolean => (
    index >= 0 &&
    index < numbers.length &&
    numbers[index] === token &&
    !claimedIndices.has(index)
);

const findPreferredTokenPosition = (
    tokens: string[],
    preferredNumberToken?: PreferredNumberToken
): number => {
    if (!preferredNumberToken) {
        return NON_NUMBER_TOKEN_INDEX;
    }

    for (let index = tokens.length - 1; index >= 0; index -= 1) {
        if (tokens[index] === preferredNumberToken.value) {
            return index;
        }
    }

    return NON_NUMBER_TOKEN_INDEX;
};

export const buildTokenHistoryFromFormula = (
    currentFormula: string,
    numbers: string[],
    previousHistory: number[] = [],
    preferredNumberToken?: PreferredNumberToken
): number[] => {
    if (!currentFormula.trim() || numbers.length === 0) {
        return [];
    }

    const tokens = currentFormula.match(FORMULA_TOKEN_PATTERN) || [];
    const claimedNumberIndices = new Set<number>();
    const preferredTokenPosition = findPreferredTokenPosition(tokens, preferredNumberToken);

    return tokens.map((token, tokenPosition) => {
        if (!NUMBER_TOKEN_PATTERN.test(token)) {
            return NON_NUMBER_TOKEN_INDEX;
        }

        const preferredIndex = preferredNumberToken?.index;
        if (
            tokenPosition === preferredTokenPosition &&
            preferredIndex !== undefined &&
            isValidNumberIndex(preferredIndex, token, numbers, claimedNumberIndices)
        ) {
            claimedNumberIndices.add(preferredIndex);
            return preferredIndex;
        }

        const previousIndex = previousHistory[tokenPosition];
        if (isValidNumberIndex(previousIndex, token, numbers, claimedNumberIndices)) {
            claimedNumberIndices.add(previousIndex);
            return previousIndex;
        }

        const matchingIndex = numbers.findIndex(
            (value, index) => value === token && !claimedNumberIndices.has(index)
        );

        if (matchingIndex === -1) {
            return NON_NUMBER_TOKEN_INDEX;
        }

        claimedNumberIndices.add(matchingIndex);
        return matchingIndex;
    });
};
